"use client";

import type { Locale } from "@/i18n/routing";
import { faCircleInfo, faXmark } from "@fortawesome/pro-solid-svg-icons";
import { useLocale, useTranslations } from "next-intl";
import { useEffect, useRef, useState } from "react";
import { Icon } from "@/components/ui/Icon";
import { useCart } from "./CartContext";

/**
 * Cart REFUSAL toast. Rendered once inside CartProvider so it survives route
 * changes (the old per-CartLink popup re-triggered on every remount). Auto-hides
 * and can be dismissed.
 *
 * ⚠️ A successful add is `CartDrawer`'s now; this only renders `refused`, a
 * view-only store rejecting the add, and `failed`, an add the server never
 * stored. They must stay apart from the drawer: it shows a cart, and showing one
 * here claims the item went in when it did not.
 */
export function AddedToCartToast() {
	const t = useTranslations("shop");
	const locale = useLocale();
	const { flash } = useCart();
	const [popup, setPopup] = useState<null | {
		name: string;
		failed: boolean;
	}>(null);
	const lastFlashRef = useRef(0);
	const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

	useEffect(() => {
		if (!flash || flash.id === lastFlashRef.current) return;
		lastFlashRef.current = flash.id;
		// A successful add opens the drawer instead.
		if (flash.kind === "added") return;
		const name = flash.name[locale as Locale] || flash.name.en || flash.name.ar || "";
		setPopup({ name, failed: flash.kind === "failed" });
		if (timerRef.current) clearTimeout(timerRef.current);
		timerRef.current = setTimeout(setPopup, 4000, null);
	}, [flash, locale]);

	useEffect(
		() => () => {
			if (timerRef.current) clearTimeout(timerRef.current);
		},
		[],
	);

	function dismiss() {
		if (timerRef.current) clearTimeout(timerRef.current);
		setPopup(null);
	}

	if (!popup) return null;
	return (
		<div
			role="alert"
			className="fixed bottom-5 end-5 z-50 w-72 rounded-xl border border-border bg-surface p-3 shadow-lg"
		>
			<div className="flex items-start gap-2">
				<span className="mt-0.5 grid h-5 w-5 shrink-0 place-items-center rounded-full bg-warning/15 text-xs text-warning">
					<Icon icon={faCircleInfo} size={14} />
				</span>
				<div className="min-w-0 flex-1">
					<p className="text-sm font-semibold text-warning">
						{popup.failed ? t("addFailed") : t("viewOnlyBanner")}
					</p>
					{popup.name && (
						<p className="mt-0.5 truncate text-sm text-muted-foreground">{popup.name}</p>
					)}
				</div>
				<button
					type="button"
					onClick={dismiss}
					aria-label={t("dismiss")}
					className="shrink-0 rounded p-0.5 text-muted-foreground hover:text-text"
				>
					<Icon icon={faXmark} size={14} />
				</button>
			</div>
		</div>
	);
}
